import WalletSummary from "@/components/wallet-summary";
import TransactionPagination from "@/components/transaction-pagination";
import { Button } from "@/components/ui/button";
import { LoaderCircle, AlertCircle, Search, ChevronLeft } from "lucide-react";
import { useSearch, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { searchWalletAddress } from "@/lib/bitcoin-api";
import { AddressData, Transaction } from "@shared/schema";
import React, { useState, useEffect } from "react";
import WalletAddressSearchBar from "@/components/WalletAddressSearchBar";
import { FavoritesButton } from "@/components/favourites";
import { subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";

// Helper function to parse date string as local date (not UTC)
function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
}

// Helper function to get date range from range parameter
function getDateRangeFromRange(range: string | null): { startDate: Date | null; endDate: Date | null } {
  const today = new Date();
  
  switch (range) {
    case 'last_week':
      return {
        startDate: subDays(today, 7),
        endDate: today
      };
    case 'last_month':
      return {
        startDate: startOfMonth(subMonths(today, 1)),
        endDate: endOfMonth(subMonths(today, 1))
      };
    case 'last_3_months':
      return {
        startDate: startOfMonth(subMonths(today, 3)),
        endDate: endOfMonth(subMonths(today, 1))
      };
    default:
      return { startDate: null, endDate: null };
  }
}

export default function AddressSearchPage() {
  const search = useSearch();
  const [_, setLocation] = useLocation();
  const searchParams = new URLSearchParams(search);
  const queryClient = useQueryClient();

  const query = searchParams.get("query") || "";
  const rangeParam = searchParams.get("range") || null;
  const startDateParam = searchParams.get("startDate") || null;
  const endDateParam = searchParams.get("endDate") || null;
  const pageToken = searchParams.get("page_token") || null;

  // Determine actual dates to use - prioritize manual dates over range
  const { startDate, endDate } = React.useMemo(() => {
    if (startDateParam && endDateParam) {
      // Manual dates provided - use those
      return {
        startDate: startDateParam,
        endDate: endDateParam
      };
    } else if (rangeParam) {
      // Range parameter provided - calculate dates
      const { startDate: rangeStart, endDate: rangeEnd } = getDateRangeFromRange(rangeParam);
      return {
        startDate: rangeStart ? rangeStart.toISOString().split('T')[0] : null,
        endDate: rangeEnd ? rangeEnd.toISOString().split('T')[0] : null
      };
    } else {
      // No parameters - use null for all transactions
      return { startDate: null, endDate: null };
    }
  }, [rangeParam, startDateParam, endDateParam]);

  // State for accumulated data across pages
  const [accumulatedData, setAccumulatedData] = useState<AddressData | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const shouldSearch = query.length >= 26;

  // Create a unique key for the current search parameters (excluding pageToken)
  const searchKey = `${query}-${startDate}-${endDate}`;
  const [currentSearchKey, setCurrentSearchKey] = useState(searchKey);

  const queryKey = shouldSearch
    ? ["/api/blockchain/address", query, startDate, endDate, pageToken]
    : ["empty-query"];

  const {
    data: searchData,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!shouldSearch) return Promise.resolve(null);
      return searchWalletAddress(query, startDate, endDate, pageToken);
    },
    enabled: shouldSearch,
    staleTime: Infinity,
  });


  const isAddressData = searchData && "address" in searchData && "transactions" in searchData;
  const latestPageData = isAddressData ? (searchData as AddressData) : null;

  // Check if this is a new search (different search parameters)
  useEffect(() => {
    if (searchKey !== currentSearchKey) {
      // New search - reset accumulated data
      setCurrentSearchKey(searchKey);
      setAccumulatedData(null);
    }
  }, [searchKey, currentSearchKey]);

  // Handle data accumulation when new page data arrives
  useEffect(() => {
    if (latestPageData) {
      if (!pageToken) {
        // First page - set as initial data
        setAccumulatedData(latestPageData);
      } else {
        // Subsequent page - append transactions to existing data
        setAccumulatedData(prevData => {
          if (!prevData) {
            return latestPageData;
          }
          
          return {
            ...latestPageData, // Use latest metadata (stats, pagination, etc.)
            transactions: [
              ...prevData.transactions, // Keep previous transactions
              ...latestPageData.transactions // Add new transactions
            ]
          };
        });
      }
      setIsLoadingMore(false);
    }
  }, [latestPageData, pageToken]);

  // Use accumulated data for display
  const addressData = accumulatedData;

  React.useEffect(() => {
    if (searchData && query) {
      sessionStorage.setItem("lastBlockSeerSearch", window.location.search);
    }
  }, [searchData, query]);

  const handleRetrySearch = () => {
    if (shouldSearch) {
      refetch();
    }
  };

  const handleBackToSearch = () => {
    setLocation("/search");
  };

  const handleNextPage = () => {
    const newParams = new URLSearchParams(searchParams);
    if (addressData?.pagination?.nextPageToken) {
      setIsLoadingMore(true);
      newParams.set("page_token", addressData.pagination.nextPageToken);
      setLocation(`/search/address?${newParams.toString()}`);
    }
  };

  const handleLoadMore = () => {
    handleNextPage();
  };

  const handlePreviousPage = () => {
    // For cumulative loading, "previous page" means start over
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("page_token");
    setAccumulatedData(null); // Reset accumulated data
    setLocation(`/search/address?${newParams.toString()}`);
  };

  const handleSearchUpdate = async (values: {
    query: string;
    startDate: Date | null;
    endDate: Date | null;
    selectedRange?: string | null;
    isManualDate?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (values.query) params.set("query", values.query.trim());

    if (values.isManualDate) {
      // Manual date selection - use startDate and endDate parameters
      if (values.startDate) params.set("startDate", values.startDate.toISOString().split('T')[0]);
      if (values.endDate) params.set("endDate", values.endDate.toISOString().split('T')[0]);
      // Don't set range parameter when using manual dates
    } else if (values.selectedRange && values.selectedRange !== 'all-transactions') {
      // Predefined range selection - use range parameter
      params.set("range", values.selectedRange);
      // Don't set startDate and endDate when using range
    }
    // If selectedRange is 'all-transactions' or null, don't set any date params

    const urlString = `/search/address?${params.toString()}`;

    // Reset accumulated data for new search
    setAccumulatedData(null);

    // Calculate the actual date strings that will be used
    let newStartDate: string | null = null;
    let newEndDate: string | null = null;

    if (values.isManualDate && values.startDate) {
      newStartDate = values.startDate.toISOString().split('T')[0];
    } else if (values.selectedRange && values.selectedRange !== 'all-transactions') {
      const rangeStart = getDateRangeFromRange(values.selectedRange).startDate;
      newStartDate = rangeStart ? rangeStart.toISOString().split('T')[0] : null;
    }

    if (values.isManualDate && values.endDate) {
      newEndDate = values.endDate.toISOString().split('T')[0];
    } else if (values.selectedRange && values.selectedRange !== 'all-transactions') {
      const rangeEnd = getDateRangeFromRange(values.selectedRange).endDate;
      newEndDate = rangeEnd ? rangeEnd.toISOString().split('T')[0] : null;
    }

    // Manually fetch the data before navigating
    setIsUpdating(true);
    try {
      const data = await searchWalletAddress(values.query.trim(), newStartDate, newEndDate, null);
      setAccumulatedData(data);
    } catch (error) {
      // Error is handled by the page's error display
    } finally {
      setIsUpdating(false);
    }

    setLocation(urlString);
  };

  // Helper function to create default name for the favorite
  const createDefaultFavoriteName = (address: string) => {
    return `Wallet Address: ${address}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex flex-col flex-grow min-h-screen">
        <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4 bg-background">
          <div className="space-y-4">
            <div className="mt-2 pt-0">
              <div className="mb-6 flex items-center justify-end">
                <FavoritesButton
                  createDefaultName={createDefaultFavoriteName}
                />
              </div>
            </div>

            <div className="mt-2 pt-0">
              <WalletAddressSearchBar 
                onUpdate={handleSearchUpdate} 
                defaultValues={{
                  query,
                  startDate: startDate ? parseLocalDate(startDate) : null,
                  endDate: endDate ? parseLocalDate(endDate) : null,
                  selectedRange: rangeParam,
                  hasManualDates: !!(startDateParam && endDateParam)
                }}
              />
            </div>

            <div className="mt-2 pt-0">
              {(isLoading || isUpdating) && (
                <div className="bg-card border border-border rounded-xl p-8 flex flex-col items-center justify-center py-12 mt-4 shadow-sm">
                  <LoaderCircle className="h-12 w-12 animate-spin text-primary" />
                  <p className="mt-4 text-card-foreground font-medium">Loading blockchain data...</p>
                </div>
              )}

              {isError && queryError && (
                <div className="bg-card border border-border rounded-lg p-6 mt-6 shadow-sm">
                  <div className="text-center py-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive mb-4">
                      <AlertCircle className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-medium text-card-foreground mb-2">
                      {(queryError as any).code === 'INVALID_ADDRESS'
                        ? `Invalid Bitcoin address: ${query}`
                        : 'Error loading transactions'}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {queryError.message}
                    </p>
                    {(queryError as any).code !== 'INVALID_ADDRESS' && (
                      <Button
                        onClick={handleRetrySearch}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        Try Again
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {addressData && addressData.transactions.length === 0 && (
                <div className="bg-card border border-border rounded-lg p-6 mt-6 shadow-sm">
                  <div className="text-center py-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted text-primary mb-4">
                      <Search className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-medium text-card-foreground mb-2">No transactions found</h3>
                    <p className="text-muted-foreground mb-4">
                      There are no transactions for this wallet address in the selected date range.
                    </p>
                    <Button 
                      onClick={handleBackToSearch} 
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Back to Search
                    </Button>
                  </div>
                </div>
              )}

              {addressData && addressData.transactions.length > 0 && (
                <div className="mt-4">
                  <WalletSummary 
                    data={addressData} 
                    onLoadMore={handleLoadMore}
                    onReset={handlePreviousPage}
                    currentPageToken={pageToken}
                    isLoadingMore={isLoadingMore}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 
